// React Query mutation hooks for signature image uploads

import { useApiClient } from '@commander/api-client';
import { useMutation } from '@tanstack/react-query';
import { createSignatureImageApi } from '../../api/emailSignatureImageApi';

/**
 * Hook to upload signature images to Google Cloud Storage
 *
 * Usage:
 * ```typescript
 * const uploadMutation = useUploadSignatureImage();
 *
 * const handleUpload = async (file: File) => {
 *   try {
 *     const gcsUrl = await uploadMutation.mutateAsync(file);
 *     // Use gcsUrl in Lexical editor
 *   } catch (error) {
 *     // Handle upload error
 *   }
 * };
 * ```
 *
 * @returns Mutation hook that accepts a File and returns GCS URL
 */
export function useUploadSignatureImage() {
  const apiClient = useApiClient();
  const imageApi = createSignatureImageApi(apiClient);

  return useMutation({
    mutationFn: (file: File) => imageApi.uploadImage(file),
    // No cache invalidation needed - images are immutable once uploaded to GCS
    // The URL is embedded directly in the Lexical JSON
  });
}
